import React, { useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Link as MuiLink,
  Chip,
  Paper,
  Divider,
  Stack,
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DeleteIcon from '@mui/icons-material/Delete';
import DirectionsCarFilledIcon from '@mui/icons-material/DirectionsCarFilled';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import { t } from 'i18next';
import { Formik, Form } from 'formik';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { updateManageByIdAction } from '../../../../actions/mangeActionV2';
import { showErrorToast, showSuccessToast } from '../../../../slices/toastSlice';
import FileUpload from '../../../../components/FileUpload';

const getName = (value) => value?.name || value || '';

const buildVehicleLabel = (vehicle = {}) => {
  const make = getName(vehicle.make);
  const model = getName(vehicle.model);

  return `${getName(vehicle.vehicleType)} ${vehicle.year ?? ''} ${make} ${model} ${getName(vehicle.color)}`.trim();
};

const getVehicleClient = (vehicle = {}, fallbackClient = null) => ({
  id: vehicle?.client?.id || vehicle?.clientId || fallbackClient?.id || null,
  name: vehicle?.client?.name || vehicle?.clientName || fallbackClient?.name || '',
  company: vehicle?.client?.company || vehicle?.clientCompany || fallbackClient?.company || '',
});

const normalizeShipmentValues = (row) => {
  const primaryClient = {
    id: row?.primaryClientId,
    name: row?.primaryClientName,
    company: row?.primaryClientCompany,
    exporter: row?.primaryClientExporterId
      ? { id: row?.primaryClientExporterId, name: row?.primaryClientExporterName }
      : null,
  };

  return {
    ...row,
    id: row?.id,
    primaryClient,
    clientExporter: row?.exporter || null,
    exporter: row?.exporter,
    Consignee: {},
    notifyParty: {},
    consigneeDock: {},
    primaryBillOfLadingFile: row?.primaryBillOfLadingFile || [],
    primaryDescription: row?.primaryDescription || row?.description || 'Used Vehicles',
    shipmentMode: row?.shipmentMode || row?.type || null,
    shipmentClients: (row?.shipmentClients || []).map((cl) => ({ client: cl })),
    containers: (row?.containers || []).map((container) => ({
      id: container.id,
      containerSize: container.containerSize ?? null,
      containerNumber: container.containerNumber ?? null,
      sealNumber: container.sealNumber ?? null,
      netWeight: container.netWeight ?? 0,
      shipmentItems: (container.shipmentItems || []).map((item) => {
        const itemVehicle = item?.vehicle || {};
        const vehicleClient = getVehicleClient(itemVehicle, primaryClient);
        const vehicle = {
          id: itemVehicle.id,
          vin: itemVehicle?.vin,
          make: { name: getName(itemVehicle?.make) },
          model: { name: getName(itemVehicle?.model) },
          year: itemVehicle?.year,
          color: { name: getName(itemVehicle?.color) },
          fuelType: { name: getName(itemVehicle?.fuelType) },
          vehicleType: { name: getName(itemVehicle?.vehicleType) },
          weight: itemVehicle?.weight || 0,
          price: itemVehicle?.price || 0,
          client: vehicleClient,
        };

        return {
          id: item.id,
          vehicle,
          consignee: item?.consignee,
          notifyParty: item?.notifyParty,
          consigneeDock: item?.consigneeDock,
          description: item?.description,
          billOfLadingFile: item?.billOfLadingFile || [],
        };
      }),
    })),
  };
};

const hasFiles = (files) => Array.isArray(files) && files.length > 0;

/**
 * UploadBillOfLadingDialog
 * A small, focused dialog component to upload Bill of Lading files for a shipment.
 * Uses Formik so it behaves like other dialogs in the codebase (e.g. MissingInfoDialog).
 */
export default function UploadBillOfLadingDialog({ open, onClose, row, onSave, handlingFetchData }) {
  const dispatch = useDispatch();

  const initialValues = useMemo(() => normalizeShipmentValues(row || {}), [row]);

  const handleSubmit = async (values, { setSubmitting }) => {
    if (!row) return;
    const payload = values;
    try {
      setSubmitting(true);
      await dispatch(updateManageByIdAction({ mode: 'shipment', id: row.id, data: payload }));
      dispatch(showSuccessToast(t('Bill of Lading uploaded')));
      if (handlingFetchData) handlingFetchData();
      if (onSave) onSave();
      onClose();
    } catch (err) {
      dispatch(showErrorToast(err?.message || t('Failed to upload Bill of Lading')));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth={initialValues.shipmentMode === 'LCL' ? 'md' : 'sm'} fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PictureAsPdfIcon sx={{ color: 'primary.main' }} />
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{t('Upload Bill of Lading')}</Typography>
            <Typography variant="caption" color="text.secondary">{t('Attach one or more PDF files for this shipment.')}</Typography>
          </Box>
        </Box>
      </DialogTitle>
      <Formik enableReinitialize initialValues={initialValues} onSubmit={handleSubmit}>
        {(formik) => {
          const files = formik.values.primaryBillOfLadingFile || [];
          const shipmentMode = formik.values.shipmentMode;
          const isLcl = shipmentMode === 'LCL';
          const containers = formik.values.containers || [];
          const hasShipmentItemFiles = containers.some((container) =>
            (container?.shipmentItems || []).some((item) => hasFiles(item?.billOfLadingFile))
          );
          const disableSave = formik.isSubmitting || (!hasFiles(files) && (!isLcl || !hasShipmentItemFiles));
          return (
            <Form>
              <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {t('Accepted: PDF. Maximum recommended size: 10MB per file.')}
                  </Typography>
                  <FileUpload
                    disabled={false}
                    label={t('Bill of Lading File')}
                    formikName="primaryBillOfLadingFile"
                    uploadName="BillOfLadingFile"
                    accept=".pdf"
                  />
                </Box>

                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: .5 }}>{t('Selected files')}</Typography>
                  {!files.length && (
                    <Chip label={t('No files selected')} color="warning" size="small" />
                  )}
                  {!!files.length && (
                    <List dense>
                      {files.map((f, idx) => {
                        const name = f?.fileName || f?.name || f?.file?.name || `file-${idx}`;
                        const url = f?.url || f?.fileUrl || f?.file?.url || null;
                        return (
                          <ListItem key={idx} disableGutters>
                            <ListItemText primary={name} secondary={url ? (
                              <MuiLink href={url} target="_blank" rel="noopener noreferrer">{t('Open')}</MuiLink>
                            ) : null} />
                            <ListItemSecondaryAction>
                              <IconButton edge="end" size="small" onClick={() => {
                                const next = files.slice();
                                next.splice(idx, 1);
                                formik.setFieldValue('primaryBillOfLadingFile', next);
                              }}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </ListItemSecondaryAction>
                          </ListItem>
                        );
                      })}
                    </List>
                  )}
                </Box>

                {isLcl && (
                  <Box>
                    <Divider sx={{ mb: 2 }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                      {t('Shipment Items Bill of Lading')}
                    </Typography>
                    <Stack spacing={1.5}>
                      {containers.map((container, containerIndex) => {
                        const shipmentItems = container?.shipmentItems || [];

                        return (
                          <Paper
                            key={container?.id || containerIndex}
                            variant="outlined"
                            sx={{ p: 1.5, borderRadius: 1, bgcolor: '#fbfdff' }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                              <LocalShippingIcon fontSize="small" color="primary" />
                              <Box sx={{ minWidth: 0 }}>
                                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                  {t('Container')} #{containerIndex + 1}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {[container?.containerNumber, container?.containerSize, container?.sealNumber].filter(Boolean).join(' | ') || '-'}
                                </Typography>
                              </Box>
                            </Box>

                            {!shipmentItems.length && (
                              <Chip label={t('No vehicles')} size="small" variant="outlined" />
                            )}

                            <Stack spacing={1.25}>
                              {shipmentItems.map((item, itemIndex) => {
                                const vehicle = item?.vehicle || {};
                                const client = getVehicleClient(vehicle);
                                const vehicleLabel = buildVehicleLabel(vehicle) || t('Vehicle');
                                const formikName = `containers.${containerIndex}.shipmentItems.${itemIndex}.billOfLadingFile`;

                                return (
                                  <Box
                                    key={item?.id || vehicle?.id || itemIndex}
                                    sx={{
                                      p: 1.25,
                                      border: '1px solid #e5eaef',
                                      borderRadius: 1,
                                      bgcolor: '#fff',
                                      display: 'flex',
                                      flexDirection: 'column',
                                      gap: 1,
                                    }}
                                  >
                                    <Box sx={{ display: 'flex', gap: 1, minWidth: 0 }}>
                                      <DirectionsCarFilledIcon fontSize="small" sx={{ color: '#0B3C5D', mt: 0.25 }} />
                                      <Box sx={{ minWidth: 0, flex: 1 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap title={vehicleLabel}>
                                          {vehicleLabel}
                                        </Typography>
                                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={0.75} sx={{ mt: 0.5 }}>
                                          <Chip size="small" variant="outlined" label={`${t('VIN')}: ${vehicle?.vin || '-'}`} />
                                          <Chip size="small" variant="outlined" label={`${t('Client')}: ${client?.name || '-'}`} />
                                          <Chip size="small" variant="outlined" label={`${t('Company')}: ${client?.company || '-'}`} />
                                        </Stack>
                                      </Box>
                                    </Box>
                                    <FileUpload
                                      disabled={false}
                                      label={t('Bill of Lading File')}
                                      formikName={formikName}
                                      uploadName="BillOfLadingFile"
                                      accept=".pdf"
                                    />
                                  </Box>
                                );
                              })}
                            </Stack>
                          </Paper>
                        );
                      })}
                    </Stack>
                  </Box>
                )}
              </DialogContent>
              <DialogActions sx={{ px: 2, py: 1 }}>
                <Button onClick={onClose} disabled={formik.isSubmitting}>{t('cancel')}</Button>
                <Button type="submit" variant="contained" color="primary" disabled={disableSave}>{t('save')}</Button>
              </DialogActions>
              {formik.isSubmitting && <LinearProgress />}
            </Form>
          );
        }}
      </Formik>
    </Dialog>
  );
}

UploadBillOfLadingDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  row: PropTypes.object,
  onSave: PropTypes.func,
  handlingFetchData: PropTypes.func,
};
